import { useState, useEffect, createRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import SectionEditForm from './SectionEditForm';
import FeatureDisabled from '../../FeatureDisabled';
import client from '../../../lib/feathers';
import { setListing } from '../../../lib/listingsSlice';

import putToStorage from '../../../lib/putToStorage';
/**
 * Name, description, logo and cover photo — how the winery introduces itself.
 *
 * Images save the moment they are chosen (an upload has already happened by the
 * time you see it, so making someone press Save afterwards only invites them to
 * navigate away and lose it). Name and description save with the form.
 */
const assetUrl = (path, params) => `${process.env.REACT_APP_ATTACHMENT_URL}/${path}?${params}`;

export default function ProfileEditForm() {
  const { lid } = useParams();
  const dispatch = useDispatch();
  const listing = useSelector((s) => s.listings.selected);
  const user = useSelector((s) => s.auth.user);
  const role = user?.role === 'admin' ? 'admin' : 'industry';

  const [name, setName] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [logoAttachment, setLogoAttachment] = useState(null);
  const [coverAttachment, setCoverAttachment] = useState(null);
  const [uploading, setUploading] = useState(null);
  // An asset that will not load — commonly a production database pointed at the
  // dev asset host, but also a genuinely missing file. Either way a broken-image
  // glyph tells the winery nothing.
  const [brokenImages, setBrokenImages] = useState({});

  const logoInput = createRef();
  const coverInput = createRef();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let row = listing?.id === lid ? listing : null;
      if (!row) {
        try {
          row = await client.service(`${role}/listing`).get(lid);
          if (cancelled) return;
          dispatch(setListing(row));
        } catch (e) {
          console.log('could not load listing', e);
          return;
        }
      }
      setName(row.name || '');
      setShortDescription(row.shortDescription || '');
      setLogoAttachment(row.logoAttachment || null);
      // The read hook synthesises a 'default' cover for listings with none of
      // their own; that is a stand-in, not something they uploaded.
      setCoverAttachment(row.profileAttachmentId === 'default' ? null : row.profileAttachment || null);
    })();
    return () => { cancelled = true; };
  }, [lid, listing?.id, dispatch]);

  if (!listing || listing.id !== lid) return null;

  /** Presign, PUT to storage, record the attachment, point the listing at it. */
  const upload = async (file, which) => {
    if (!file) return;
    setUploading(which);
    try {
      const id = uuidv4();
      const urls = await client.service(`${role}/attachment-upload`).create({
        'file-name': `listing-images/${id}`,
        'file-type': file.type,
        id: lid,
      });

      await putToStorage(urls.signedRequest, file, { headers: { 'Content-Type': file.type } });

      const attachment = await client.service(`${role}/attachment`).create({
        id, path: urls.path, type: 'photo', mime: file.type, listingId: lid,
      });

      const key = which === 'logo' ? 'logoAttachmentId' : 'profileAttachmentId';
      await client.service(`${role}/listing`).patch(lid, { [key]: attachment.id });

      if (which === 'logo') setLogoAttachment(attachment);
      else setCoverAttachment(attachment);
      dispatch(setListing({ ...listing, [key]: attachment.id }));
      toast.success(which === 'logo' ? 'Logo updated' : 'Cover photo updated');
    } catch (err) {
      toast.error(err.message || 'Could not upload that image.');
    } finally {
      setUploading(null);
    }
  };

  const remove = async (which) => {
    const label = which === 'logo' ? 'logo' : 'cover photo';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove your ${label}?`)) return;
    try {
      await client.service(`${role}/listing-remove-attachment`)
        .update(lid, { key: which === 'logo' ? 'logo' : 'profile' });
      if (which === 'logo') setLogoAttachment(null); else setCoverAttachment(null);
      toast.success(`${label[0].toUpperCase()}${label.slice(1)} removed`);
    } catch (err) {
      toast.error(err.message || 'Could not remove that image.');
    }
  };

  const imageBlock = (which, attachment, previewParams, previewClass) => (
    <div>
      <span className="block text-sm font-medium text-gray-700">
        {which === 'logo' ? 'Logo' : 'Cover photo'}
      </span>
      <div className="mt-2 flex items-center gap-4">
        {attachment?.path && !brokenImages[which] ? (
          <img
            src={assetUrl(attachment.path, previewParams)}
            alt={which === 'logo' ? 'Listing logo' : 'Listing cover'}
            className={previewClass}
            onError={() => setBrokenImages((b) => ({ ...b, [which]: true }))}
          />
        ) : (
          <div className={`${previewClass} bg-gray-100 flex items-center justify-center text-center text-xs text-gray-400 px-2`}>
            {attachment?.path ? 'Image unavailable' : 'None yet'}
          </div>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => (which === 'logo' ? logoInput : coverInput).current.click()}
            disabled={uploading === which}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {uploading === which ? 'Uploading…' : attachment?.path ? 'Replace' : 'Upload'}
          </button>
          {attachment?.path && (
            <button
              type="button"
              onClick={() => remove(which)}
              className="px-3 py-2 text-sm text-gray-500 hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>
      <input
        ref={which === 'logo' ? logoInput : coverInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => upload(e.target.files[0], which)}
      />
    </div>
  );

  return (
    <SectionEditForm
      listing={listing}
      title="Profile"
      description="Your name, how you describe yourself, and the images visitors see first."
      backTo={`/${role}/listings/${lid}`}
      buildPatch={() => ({ name, shortDescription })}
      onSaved={(updated) => dispatch(setListing({
        ...listing,
        name: updated.name ?? name,
        shortDescription: updated.shortDescription ?? shortDescription,
      }))}
    >
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-800 focus:ring-red-800 sm:text-sm"
        />
      </div>

      <FeatureDisabled feature="SHORT_DESCRIPTION" tier={listing.subscriptionTier}>
        <div>
          <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700">
            Short description
          </label>
          <textarea
            id="shortDescription"
            rows={4}
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            placeholder="A couple of sentences on what makes a visit here worth the drive."
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-800 focus:ring-red-800 sm:text-sm"
          />
        </div>
      </FeatureDisabled>

      <FeatureDisabled feature="LOGO" tier={listing.subscriptionTier}>
        {imageBlock('logo', logoAttachment, 'h=96&w=96', 'h-16 w-16 rounded object-cover')}
      </FeatureDisabled>

      {imageBlock('cover', coverAttachment, 'w=600&f=inside', 'h-24 w-40 rounded object-cover')}
    </SectionEditForm>
  );
}
